import { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, CircleDollarSign, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DataTable } from "@/components/common/DataTable";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MigracionLineaCampos, MIG_LINEA_VACIA, bodyMigracionLinea, errorMigracionLinea } from "@/components/lineas/MigracionLineaCampos";

const fmt$ = (n) => (typeof n === "number" ? n : Number(n || 0)).toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });
const fmtF = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" }); } catch { return iso; } };

const ESTATUS_STYLES = {
  "Por desembolsar": "bg-amber-100 text-amber-700",
  "Vigente": "bg-sky-100 text-sky-700",
  "Vencido": "bg-rose-100 text-rose-700",
  "Liquidado": "bg-emerald-100 text-emerald-700",
  "Cancelado": "bg-slate-200 text-slate-700",
};

export default function DisposicionesTab({ linea, onChange, bloqueo }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ monto: "", plazo: "", medio_dispersion: "SPEI", cuenta_bancaria: "" });
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const [migDisp, setMigDisp] = useState(MIG_LINEA_VACIA);
  const [disp, setDisp] = useState({ limite: linea.limite_autorizado || 0, dispuesto: linea.dispuesto || 0, disponible: linea.disponible || 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: lst }, { data: d }] = await Promise.all([
        api.get(`/lineas/${linea.id}/disposiciones`),
        api.get(`/lineas/${linea.id}/disponible`),
      ]);
      setRows(Array.isArray(lst) ? lst : []);
      setDisp(d);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [linea.id]);

  useEffect(() => { load(); }, [load]);

  const montoNum = Number(form.monto || 0);
  const excedeDisponible = montoNum > 0 && montoNum > disp.disponible;
  const puedeEnviar = montoNum > 0 && Number(form.plazo || 0) > 0 && !excedeDisponible && !busy;

  const crear = async () => {
    const errMig = errorMigracionLinea(migDisp);
    if (errMig) { toast.error(errMig); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/lineas/${linea.id}/disponer`, {
        monto: Number(form.monto), plazo: Number(form.plazo),
        medio_dispersion: form.medio_dispersion, cuenta_bancaria: form.cuenta_bancaria,
        ...bodyMigracionLinea(migDisp),
      });
      toast.success(`Disposición ${data.numero_credito} creada. Pasa a Mesa de Control.`);
      setShowForm(false);
      setForm({ monto: "", plazo: "", medio_dispersion: "SPEI", cuenta_bancaria: "" });
      setMigDisp(MIG_LINEA_VACIA);
      await load();
      onChange && onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const cols = [
    { key: "numero_credito", header: "Disposición", render: (r) => <span className="font-mono text-sm">{r.numero_credito}</span> },
    { key: "created_at", header: "Fecha", render: (r) => fmtF(r.created_at) },
    { key: "monto_original", header: "Monto", render: (r) => <span className="font-semibold">{fmt$(r.monto_original)}</span> },
    { key: "plazo", header: "Plazo", render: (r) => `${r.plazo} pagos` },
    { key: "saldo_insoluto", header: "Saldo insoluto", render: (r) => fmt$(r.saldo_insoluto) },
    { key: "estatus", header: "Estatus", render: (r) => {
        const cls = ESTATUS_STYLES[r.estatus] || "bg-slate-100 text-slate-700";
        return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{r.estatus}</span>;
      } },
  ];

  const noOperable = ["Vencida", "Cancelada"].includes(linea.estatus);
  const suspendida = linea.estatus === "Suspendida";
  // Fase 6 (feb-2026) — Bloqueo por mora consultado desde el detalle. Cuando
  // `bloqueo?.bloqueada` deshabilitamos el botón de Nueva disposición con el mismo
  // motivo que devolvería `disponer` (helper compartido: ver `_estado_bloqueo_linea`).
  const bloqueadaMora = !!bloqueo?.bloqueada;

  return (
    <div className="space-y-4" data-testid="disposiciones-tab">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Disponible en la línea</p>
            <p className="mt-1 text-4xl font-bold text-emerald-900" data-testid="linea-disponible-grande">{fmt$(disp.disponible)}</p>
            <p className="mt-1 text-xs text-emerald-800">
              Límite {fmt$(disp.limite)} · Dispuesto {fmt$(disp.dispuesto)}
            </p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            disabled={noOperable || suspendida || bloqueadaMora || disp.disponible <= 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="btn-nueva-disposicion"
            title={bloqueadaMora ? bloqueo?.motivo : undefined}
          >
            <Plus className="mr-2 h-4 w-4" /> Nueva disposición
          </Button>
        </div>
        {suspendida && <p className="mt-3 text-xs text-amber-800">La línea está suspendida. Reactívala para disponer.</p>}
        {noOperable && <p className="mt-3 text-xs text-rose-800">La línea está {linea.estatus}. No se pueden crear disposiciones.</p>}
        {bloqueadaMora && (
          <p className="mt-3 text-xs font-medium text-rose-800" data-testid="disp-bloqueo-mora-motivo">
            🔒 {bloqueo.motivo}
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando disposiciones…</div>
      ) : (
        <DataTable
          columns={cols}
          rows={rows}
          empty={
            <div className="py-8 text-center">
              <CircleDollarSign className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm text-slate-600">Aún no hay disposiciones sobre esta línea.</p>
            </div>
          }
          testid="tabla-disposiciones"
        />
      )}

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) setShowForm(false); }}>
        <DialogContent data-testid="dialog-nueva-disposicion">
          <DialogHeader><DialogTitle>Nueva disposición</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-emerald-50 p-4">
              <p className="text-xs font-semibold uppercase text-emerald-700">Disponible ahora</p>
              <p className="mt-1 text-3xl font-bold text-emerald-900">{fmt$(disp.disponible)}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Monto</Label>
                <Input type="number" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} placeholder={`Entre ${fmt$(linea.disposicion_min)} y ${fmt$(Math.min(linea.disposicion_max, disp.disponible))}`} data-testid="disp-monto" />
                {excedeDisponible && <p className="mt-1 text-xs text-rose-700">Excede el disponible ({fmt$(disp.disponible)}).</p>}
              </div>
              <div>
                <Label className="text-xs">Plazo (pagos)</Label>
                <Input type="number" value={form.plazo} onChange={(e) => setForm({ ...form, plazo: e.target.value })} placeholder={`Máx ${linea.plazo_max_disposicion}`} data-testid="disp-plazo" />
              </div>
              <div>
                <Label className="text-xs">Medio de dispersión</Label>
                <Input value={form.medio_dispersion} onChange={(e) => setForm({ ...form, medio_dispersion: e.target.value })} data-testid="disp-medio" />
              </div>
              <div>
                <Label className="text-xs">Cuenta destino (CLABE)</Label>
                <Input value={form.cuenta_bancaria} onChange={(e) => setForm({ ...form, cuenta_bancaria: e.target.value })} data-testid="disp-cuenta" />
              </div>
            </div>
            {user?.role === "admin_entidad" && (
              <MigracionLineaCampos value={migDisp} onChange={setMigDisp} tipo="disposicion"
                fechaMin={String(linea.fecha_inicio || "").slice(0, 10)} prefijo="disp" />
            )}
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">
              <p><strong>Hereda de la línea:</strong> tasa ordinaria {Number(linea.tasa_ordinaria).toFixed(2)}%, tasa moratoria {Number(linea.tasa_moratoria).toFixed(2)}%, periodicidad {linea.periodicidad}, amortización {linea.tipo_amortizacion}, IVA intereses {linea.iva_intereses_credito}.</p>
              {linea.comision_disposicion > 0 && <p className="mt-1">Comisión por disposición: {linea.comision_disposicion}{linea.comision_disposicion_tipo === "porcentaje" ? "%" : " MXN"} (se calcula, no se cobra aún).</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} data-testid="disp-cancel">Cerrar</Button>
            <Button onClick={crear} disabled={!puedeEnviar} className="bg-emerald-600 hover:bg-emerald-700" data-testid="disp-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Crear disposición
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
